/* eslint-disable import/no-extraneous-dependencies */
import {
  initializeFaro,
  getWebInstrumentations,
  ReactIntegration,
  LogLevel,
  type Faro,
  type TransportItem,
  TransportItemType,
} from '@grafana/faro-react';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';

import { version } from '../../package.json';

export { LogLevel };

let faroInstance: Faro | undefined;

const SAMPLE_RATE_BY_ENV: Record<string, number> = {
  development: 1,
  staging: 0.1,
  'pre-prod': 0.01,
  production: 0.01,
};

const NETWORK_ERROR_PATTERNS = [
  'failed to fetch',
  'networkerror',
  'network request failed',
  'fetch failed',
  'err_internet_disconnected',
  'err_network_changed',
  'err_connection_refused',
  'err_connection_reset',
  'err_name_not_resolved',
  'net::err_failed',
  'load failed',
  'econnaborted',
  'etimedout',
];

const IGNORE_ERROR_PATTERNS = [
  'resizeobserver loop',
  'script error',
  'non-error promise rejection',
  'cancelled',
  'aborted',
];

const isNetworkError = (message: string): boolean =>
  NETWORK_ERROR_PATTERNS.some((p) => message.includes(p));

const shouldIgnoreError = (message: string): boolean => {
  if (!message) return false;
  const lower = message.toLowerCase();
  return isNetworkError(lower) || IGNORE_ERROR_PATTERNS.some((p) => lower.includes(p));
};

const getPayloadErrorMessage = (payload: Record<string, unknown>): string => {
  if (typeof payload?.value === 'string') return payload.value;
  if (payload?.value instanceof Error) return payload.value.message;
  if (typeof payload?.message === 'string') return payload.message;
  if (payload?.message != null) return String(payload.message);
  return '';
};

export default function initFaro(): Faro | undefined {
  if (typeof window === 'undefined') return undefined;
  if (faroInstance) return faroInstance;

  const collectorUrl = process.env.NEXT_PUBLIC_FARO_COLLECTOR_URL;
  const apiKey = process.env.NEXT_PUBLIC_FARO_API_KEY;
  const appEnv = process.env.NEXT_PUBLIC_ENVIRONMENT;

  if (!collectorUrl || appEnv === 'local') return undefined;

  const sampleRate = SAMPLE_RATE_BY_ENV[appEnv || ''] ?? 0.01;

  try {
    faroInstance = initializeFaro({
      url: collectorUrl,
      ...(apiKey && { apiKey }),

      app: {
        name: 'institute-of-innovative-skills-nextjs',
        version: version ?? '1.0.0',
        environment: appEnv,
      },

      sessionTracking: {
        enabled: true,
        persistent: true,
      },

      ignoreUrls: [/localhost/, /127\.0\.0\.1/],

      instrumentations: [
        ...getWebInstrumentations({ captureConsole: false }),
        new ReactIntegration(),
        new TracingInstrumentation(),
      ],

      beforeSend: (event: TransportItem): TransportItem | null => {
        const payload = event.payload as Record<string, unknown>;
        const errorMessage = getPayloadErrorMessage(payload);

        if (shouldIgnoreError(errorMessage)) return null;

        if (event.type === TransportItemType.EXCEPTION) return event;

        if (Math.random() > sampleRate) return null;

        return event;
      },
    });

    return faroInstance;
  } catch {
    return undefined;
  }
}

export const setFaroUser = (user: { id?: string; email?: string }) => {
  if (!faroInstance || !user?.id) return;
  faroInstance.api.setUser({
    id: user.id,
    email: user.email || '',
    attributes: { framework: 'nextjs', app_type: 'ssr' },
  });
};

export const resetFaroUser = () => {
  if (!faroInstance) return;
  faroInstance.api.resetUser();
  faroInstance.api.resetSession();
};

export const pushFaroError = (error: Error, context?: Record<string, string>) => {
  faroInstance?.api.pushError(error, { context });
};

export const pushFaroLog = (
  message: string,
  context?: Record<string, string>,
  level: LogLevel = LogLevel.WARN
) => {
  faroInstance?.api.pushLog([message], { context, level });
};

export const pushFaroEvent = (name: string, attributes?: Record<string, string>) => {
  faroInstance?.api.pushEvent(name, attributes);
};
